/** Habit logging.
 *
 *  A log is an append-and-tombstone ledger row with no revision, so these
 *  endpoints take an `Idempotency-Key` and **no** `If-Match` — there is nothing
 *  to match on.
 *
 *  Keys are minted fresh per tap, and that is deliberate. An earlier version
 *  derived a stable key from `(habit, date, resulting count)`, reasoning that a
 *  double-tap should be idempotent. It made the grid silently broken: log, undo,
 *  log again reused the first key, so the third tap replayed a receipt and
 *  changed nothing while returning 201. Measured — count went 0, 1, 0, then
 *  stayed 0.
 *
 *  A tap is a distinct logical mutation, so it gets a distinct key. Only a retry
 *  of one tap should be idempotent, and there is no retry loop here.
 *
 *  What AGENTS.md requires is narrower than it first reads: because
 *  `tombstone_log` keys its receipt on the habit alone and hashes the journal
 *  date into the request, one key must never be reused across *dates* — a
 *  replay against a different date is a 409, not a second delete. Random keys
 *  satisfy that trivially.
 */

import { ApiError, request, type Session } from './client';
import { newIdempotencyKey } from './ids';
import type { WireTracker } from './wire';

/** Add one unit to this habit on this date. */
export async function logUnit(
  session: Session,
  habitId: string,
  date: string,
): Promise<void> {
  await request(session, `/v1/habits/${habitId}/logs`, {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
    body: { journal_date: date, count: 1 },
  });
}

/** Undo the most recent unit on this habit and date.
 *
 *  Returns false when there was nothing to undo. Delete-latest answers 404 on an
 *  empty date, so the caller checks the count first rather than painting an API
 *  error over a no-op — the same reason the TUI refuses the keystroke.
 */
export async function undoUnit(
  session: Session,
  habitId: string,
  date: string,
  currentCount: number,
): Promise<boolean> {
  if (currentCount <= 0) return false;
  try {
    await request(session, `/v1/habits/${habitId}/logs:delete-latest`, {
      method: 'POST',
      idempotencyKey: newIdempotencyKey(),
      body: { journal_date: date },
    });
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return false;
    throw error;
  }
}

export async function tracker(
  session: Session,
  from: string,
  to: string,
): Promise<WireTracker> {
  return (await request<WireTracker>(session, `/v1/habits/tracker?from=${from}&to=${to}`)).body;
}
